import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = ({ t, currentLang }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    hotel_name: '',
    email: '',
    message: '',
    special_requests: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Custom validation messages
    const form = e.target;
    const inputs = form.querySelectorAll('input[required], textarea[required]');
    
    for (let input of inputs) {
      if (!input.validity.valid) {
        if (input.validity.valueMissing) {
          input.setCustomValidity(t.contact.form.validation.required);
        } else if (input.validity.typeMismatch && input.type === 'email') {
          input.setCustomValidity(t.contact.form.validation.email);
        }
        input.reportValidity();
        return;
      }
    }
    
    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        language: currentLang
      };
      
      // Save language to localStorage before navigating
      localStorage.setItem('biteSiteLang', currentLang);
      
      await axios.post(`${API}/contact`, payload);
      
      // Navigate to success page instead of showing toast
      navigate('/message-received');
      
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Something went wrong. Please try again or email directly.');
      setIsSubmitting(false);
    }
  };

  // Reset custom validity on input
  const handleInputChange = (e) => {
    e.target.setCustomValidity('');
    handleChange(e);
  };

  return (
    <section id="contact" className="py-24 bg-slate-950">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t.contact.title}
          </h2>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-white mb-2 block">
                  {t.contact.form.name}
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t.contact.form.namePlaceholder}
                  required
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-slate-500"
                />
              </div>

              <div>
                <Label htmlFor="hotelName" className="text-white mb-2 block">
                  {t.contact.form.hotelName}
                </Label>
                <Input
                  id="hotelName"
                  name="hotel_name"
                  value={formData.hotel_name}
                  onChange={handleInputChange}
                  placeholder={t.contact.form.hotelPlaceholder}
                  required
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-slate-500"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-white mb-2 block">
                  {t.contact.form.email}
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={t.contact.form.emailPlaceholder}
                  required
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-slate-500"
                />
              </div>

              <div>
                <Label htmlFor="message" className="text-white mb-2 block">
                  {t.contact.form.message}
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder={t.contact.form.messagePlaceholder}
                  required
                  rows={4}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-slate-500 resize-none"
                />
              </div>

              <div>
                <Label htmlFor="specialRequests" className="text-white mb-2 block">
                  {t.contact.form.specialRequests}
                </Label>
                <Textarea
                  id="specialRequests"
                  name="special_requests"
                  value={formData.special_requests}
                  onChange={handleChange}
                  placeholder={t.contact.form.specialRequestsPlaceholder}
                  rows={3}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-slate-500 resize-none"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-white text-slate-900 hover:bg-slate-100 py-6 text-lg font-semibold rounded-lg transition-all duration-200 hover:scale-105"
              >
                {isSubmitting ? t.contact.form.sending : t.contact.form.submit}
                <Send className="ml-2 h-5 w-5" />
              </Button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-8">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
                  <Mail className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-2">Email</h3>
                  <a
                    href={`mailto:${t.contact.info.email}`}
                    className="text-slate-400 hover:text-white transition-colors duration-200"
                  >
                    {t.contact.info.email}
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-8">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
                  <Phone className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-2">{t.contact.info.phoneLabel}</h3>
                  <a
                    href={`tel:${t.contact.info.phone.replace(/\s/g, '')}`}
                    className="text-slate-400 hover:text-white transition-colors duration-200"
                  >
                    {t.contact.info.phone}
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-8">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-2">{t.contact.info.locationLabel}</h3>
                  <p className="text-slate-400">
                    {t.contact.info.location}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-8">
              <h3 className="text-white font-semibold text-lg mb-4">{t.contact.bio.name}</h3>
              <p className="text-slate-400 leading-relaxed">
                {t.contact.bio.text}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
